/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import { ApplicationView } from '@gh/builder-shared-data';
import { HelpSystem } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { HELP_DEV_MODE, HELP_URL, REGION_CODE } from '@gh/config';
import { isNotNil, openInNewWindow, substitute } from '@gh/core-util';
import { isNil, isObject, isString } from 'lodash';

import * as officeHelpLinks from './help-links/office-help-links.json';
import * as masterHelpLinks from './help-links/master-help-links.json';
import * as corporateHelpLinks from './help-links/corporate-help-links.json';

/**
 * Set of available Help IDs can be found in this document
 * https://docs.google.com/spreadsheets/d/1Iqn7vawvOgOMbxFZTtfKSAF3lfqwAH-Xc3cA_MZBNlY/edit?usp=sharing
 * (Use "Export Help Links" Add-On available under "Site Map Tools" menu in this document)
 */
const HELP_LINKS = {
  [ApplicationView.Office]: officeHelpLinks,
  [ApplicationView.Master]: masterHelpLinks,
  [ApplicationView.Corporate]: corporateHelpLinks,
};

export const getHelpPath = (view: ApplicationView, helpId: ID) => {
  if (HELP_DEV_MODE) {
    return helpId.replace(/\//g, '-');
  }
  const viewLinks = HELP_LINKS[view];
  // find exported links using module-independent way:
  // in DEV mode JSON is exported under the '*', but in PROD mode JSON is exported under 'default' name
  const exportedViewLinks = isObject(viewLinks['default']) ? viewLinks['default'] : viewLinks;
  const link = exportedViewLinks ? exportedViewLinks[helpId] : void 0;
  return isNil(link) || isString(link) ? link : link[REGION_CODE];
};

@Injectable()
export class BuilderHelpSystem implements HelpSystem {
  constructor(private userContext: BuilderUserContext) {
  }

  open(helpId?: ID): void {
    if (HELP_URL) {
      const path = helpId && getHelpPath(this.userContext.applicationView, helpId) || '';
      openInNewWindow(substitute(HELP_URL, { path }));
    }
  }

  has(helpId: ID): boolean {
    return isNotNil(getHelpPath(this.userContext.applicationView, helpId));
  }
}
